import * as React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useSession } from '@hooks';
import { loginStatusVar } from '@context';
import { NAVIGATION_MAP, CALENDAR_LAST_KNOWN_DATE_KEY } from '@utils';

type NavKey =
  | 'spots'
  | 'events'
  | 'search'
  | 'calendar'
  | 'menu'
  | 'me'
  | 'booking'
  | 'dashboard'
  | 'users'
  | 'accounts'
  | 'report';

type NavItem = {
  key: NavKey;
  label?: string;
  icon: React.ReactElement;
  to?: string;
  onClick?: () => void;
};

export function BottomNavNative() {
  const { pathname } = useLocation();
  const { account } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const loginStatus = useReactiveVar(loginStatusVar);
  const navigate = useNavigate();
  const theme = useTheme();

  const role = account?.__typename ?? 'Guest';
  const lastSearch = localStorage.getItem('last-search');
  const showBottomNav = !pathname.startsWith('/register/spot');

  React.useEffect(() => {
    const lastKnownDate = localStorage.getItem(CALENDAR_LAST_KNOWN_DATE_KEY);
    if (!lastKnownDate) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const formattedToday = `${yyyy}-${mm}-${dd}`;
      localStorage.setItem(CALENDAR_LAST_KNOWN_DATE_KEY, formattedToday);
    }
  }, []);

  const items = React.useMemo<NavItem[]>(() => {
    if (account === null) {
      return [
        {
          key: 'spots',
          label: 'Spots',
          to: NAVIGATION_MAP.Guest.spots,
          icon: <theme.icons.explore fontSize="small" />,
        },
        {
          key: 'search',
          label: t('word.Search').toLowerCase(),
          to: lastSearch ?? NAVIGATION_MAP.Guest.search,
          icon: <theme.icons.search fontSize="small" />,
        },
        {
          key: 'me',
          label: t('account', { ns: 'cta' }),
          onClick: () => {
            loginStatusVar({ ...loginStatus, shouldLogFirst: true });
          },
          icon: <theme.icons.account fontSize="small" />,
        },
      ];
    }

    if (account?.__typename === 'Consumer') {
      return [
        {
          key: 'spots',
          to: NAVIGATION_MAP.Consumer.spots,
          icon: <theme.icons.explore fontSize="small" />,
        },
        {
          key: 'search',
          to: lastSearch ?? `${NAVIGATION_MAP.Consumer.search}`,
          icon: <theme.icons.search fontSize="small" />,
        },
        {
          key: 'calendar',
          to: `${NAVIGATION_MAP.Consumer.calendar}?date=${localStorage.getItem(CALENDAR_LAST_KNOWN_DATE_KEY) ?? ''}`,
          icon: <theme.icons.event fontSize="small" />,
        },
        {
          key: 'me',
          to: NAVIGATION_MAP.Consumer.me,
          onClick: () => {
            const type = account.__typename.toLowerCase()[0];
            const { slug } = account;
            const next = new URLSearchParams(searchParams);
            next.set(type, slug);
            setSearchParams(next, { preventScrollReset: true });
          },
          icon: (
            <Avatar src={account.avatar?.url} alt="avatar" sx={{ width: 24, height: 24 }}>
              {account.name[0]}
            </Avatar>
          ),
        },
      ];
    }

    if (account?.__typename === 'Spot') {
      return [
        {
          key: 'spots',
          to: NAVIGATION_MAP.Spot.dashboard,
          icon: <theme.icons.explore fontSize="small" />,
        },
        {
          key: 'booking',
          to: `${NAVIGATION_MAP.Spot.booking}?date=${localStorage.getItem(CALENDAR_LAST_KNOWN_DATE_KEY) ?? ''}`,
          icon: <theme.icons.booking fontSize="small" />,
        },
        {
          key: 'events',
          to: `${NAVIGATION_MAP.Spot.event}?date=${localStorage.getItem(CALENDAR_LAST_KNOWN_DATE_KEY) ?? ''}`,
          icon: <theme.icons.event fontSize="small" />,
        },
        {
          key: 'me',
          to: NAVIGATION_MAP.Spot.me,
          onClick: () => {
            const type = account.__typename.toLowerCase()[0];
            const { slug } = account;
            const next = new URLSearchParams(searchParams);
            next.set(type, slug);
            setSearchParams(next, { preventScrollReset: true });
          },
          icon: (
            <Avatar src={account.avatar?.url} alt="avatar" sx={{ width: 24, height: 24 }}>
              {account.name[0]}
            </Avatar>
          ),
        },
      ];
    }

    if (account?.__typename === 'Support') {
      return [
        {
          key: 'dashboard',
          to: NAVIGATION_MAP.Support.dashboard,
          icon: <theme.icons.dashboard fontSize="small" />,
        },
        {
          key: 'users',
          to: NAVIGATION_MAP.Support.users,
          icon: <theme.icons.people fontSize="small" />,
        },
        {
          key: 'accounts',
          to: NAVIGATION_MAP.Support.accounts,
          icon: <theme.icons.accounts fontSize="small" />,
        },
        {
          key: 'events',
          to: NAVIGATION_MAP.Support.events,
          icon: <theme.icons.event fontSize="small" />,
        },
        {
          key: 'report',
          to: NAVIGATION_MAP.Support.reports,
          icon: <theme.icons.report fontSize="small" />,
        },
      ];
    }

    // Fallback safe
    return [
      {
        key: 'spots',
        to: '/',
        icon: <theme.icons.explore fontSize="small" />,
      },
    ];
  }, [account, loginStatus, navigate, role, searchParams, setSearchParams, t, theme.icons]);

  const activeKey = React.useMemo(() => pickActiveKey(pathname, items), [pathname, items]);

  const handleChange = (_: React.SyntheticEvent, nextKey: NavKey) => {
    const item = items.find((x) => x.key === nextKey);
    if (!item) return;

    if (item.onClick) {
      item.onClick();
      return;
    }

    if (item.to) {
      navigate(item.to, { preventScrollReset: true, flushSync: true, viewTransition: true });
    }
  };

  return (
    <Slide direction="up" in={showBottomNav} mountOnEnter unmountOnExit>
      <Box
        id="bottomnav"
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          pb: Capacitor.isNativePlatform() ? 'env(safe-area-inset-bottom)' : 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to top, ${theme.palette.background.default} 50%, rgba(255,255,255,0) 100%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 'min(720px, calc(100% - 24px))',
            pointerEvents: 'auto',
            borderRadius: 999,
            border: '1px solid rgba(75, 75, 75, 1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Tabs
            value={activeKey}
            onChange={handleChange}
            variant="fullWidth"
            textColor="inherit"
            TabIndicatorProps={{
              children: <span className="MuiTabs-indicatorSpan" />,
            }}
            sx={{
              position: 'relative',
              backgroundColor: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              borderRadius: 999,

              '& .MuiTabs-flexContainer': {
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 1,
              },

              '& .MuiTabs-indicator': {
                height: '100%',
                backgroundColor: 'transparent',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 0,
              },

              '& .MuiTabs-indicatorSpan': {
                width: 'calc(100% - 10px)',
                height: 'calc(100% - 10px)',
                borderRadius: 999,
                backgroundColor: 'rgba(70, 70, 70, 1)',
                border: '1px outset rgba(255, 255, 255, .5)',
              },

              '& .MuiTab-root': {
                position: 'relative',
                zIndex: 1,
              },

              '& .MuiTab-iconWrapper': {
                position: 'relative',
                zIndex: 1,
              },
            }}
          >
            {items.map((it) => (
              <Tab
                key={it.key}
                id={`bottomnav-tab-${it.key}`}
                iconPosition="top"
                disableRipple
                value={it.key}
                icon={it.icon}
                label={it.label}
                sx={{
                  height: 55,
                  minHeight: 55,
                  textTransform: 'none',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.55)',
                  opacity: 1,
                  '&.Mui-selected': {
                    color: '#FFFFFF',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 30,
                    mb: 0,
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>
      </Box>
    </Slide>
  );
}

function pickActiveKey(pathname: string, items: NavItem[]): NavKey {
  // Match le plus long préfixe pour que /me/calendar/booking active "booking"
  let best: { key: NavKey; len: number } | null = null;

  for (const it of items) {
    if (!it.to) continue;

    const [to] = it.to.split('?');

    const exactOrPrefix =
      pathname === to ||
      (pathname.startsWith(to) && (to === '/' || pathname[to.length] === '/' || to.endsWith('/')));

    if (!exactOrPrefix) continue;

    if (!best || it.to.length > best.len) {
      best = { key: it.key, len: it.to.length };
    }
  }

  const activeKey = best?.key ?? items[0]?.key ?? 'spots';

  return activeKey;
}
